import { deadlineFromRemainingSeconds } from "./sessionTiming.mjs";

let accessToken = null;
let accessTokenRevision = 0;
let sessionEndInProgress = false;
let sessionExpirationTimer = null;
let sessionExpirationAt = null;
let idleDeadlineAt = null;
const listeners = new Set();
const timingListeners = new Set();
const AUTH_EVENT_KEY = "bp-auth-event";
const channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("bp-auth") : null;

const emit = () => {
  window.dispatchEvent(new Event("auth-token-changed"));
  listeners.forEach((listener) => listener(accessToken));
};

const normalize = (token) => {
  if (!token) return null;
  return token.startsWith("Bearer ") ? token.slice(7) : token;
};

const emitTiming = () => {
  const timing = { sessionExpirationAt, idleDeadlineAt };
  timingListeners.forEach((listener) => listener(timing));
};

const armSessionExpiration = (expiresAt) => {
  if (sessionExpirationTimer) clearTimeout(sessionExpirationTimer);
  sessionExpirationTimer = null;
  if (expiresAt === null || expiresAt === undefined || expiresAt === "") {
    sessionExpirationAt = null;
    return true;
  }
  sessionExpirationAt = Number.isFinite(Number(expiresAt)) ? Number(expiresAt) : null;
  if (sessionExpirationAt === null) return true;
  const delay = sessionExpirationAt - Date.now();
  if (delay <= 0) {
    sessionExpirationAt = null;
    signalSessionEnded("session_expired");
    return false;
  }
  sessionExpirationTimer = setTimeout(
    () => signalSessionEnded("session_expired"),
    Math.min(delay, 2_147_483_647)
  );
  return true;
};

const publishTiming = () => {
  const event = {
    type: "session-timing",
    sessionExpirationAt,
    idleDeadlineAt,
  };
  if (channel) {
    channel.postMessage(event);
  } else {
    localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({ ...event, at: Date.now() }));
    localStorage.removeItem(AUTH_EVENT_KEY);
  }
};

const applyTiming = (
  { nextSessionExpirationAt, nextIdleDeadlineAt },
  { broadcast = true } = {}
) => {
  if (nextSessionExpirationAt !== undefined) {
    if (!armSessionExpiration(nextSessionExpirationAt)) return;
  }
  if (nextIdleDeadlineAt !== undefined) {
    idleDeadlineAt =
      nextIdleDeadlineAt === null || !Number.isFinite(Number(nextIdleDeadlineAt))
        ? null
        : Number(nextIdleDeadlineAt);
  }
  emitTiming();
  if (broadcast) publishTiming();
};

export const getAccessToken = () => accessToken;
export const getAccessTokenRevision = () => accessTokenRevision;
export const getIdleDeadlineAt = () => idleDeadlineAt;
export const getSessionExpirationAt = () => sessionExpirationAt;

export const getAuthorizationToken = () => (accessToken ? `Bearer ${accessToken}` : null);

export const setAuthenticationTiming = (
  { sessionExpiresIn, idleExpiresIn, idleFallbackSeconds } = {},
  { broadcast = true } = {}
) => {
  const timing = {};
  if (sessionExpiresIn !== undefined) {
    const deadline = deadlineFromRemainingSeconds(sessionExpiresIn);
    if (deadline !== null) timing.nextSessionExpirationAt = deadline;
  }

  if (idleExpiresIn !== undefined) {
    timing.nextIdleDeadlineAt =
      idleExpiresIn === null ? null : deadlineFromRemainingSeconds(idleExpiresIn);
  } else if (idleFallbackSeconds !== undefined) {
    const fallback = deadlineFromRemainingSeconds(idleFallbackSeconds);
    if (fallback !== null) timing.nextIdleDeadlineAt = fallback;
  }

  if (Object.keys(timing).length === 0) return null;
  applyTiming(timing, { broadcast });
  return {
    sessionExpirationAt,
    idleDeadlineAt,
  };
};

export const setAccessToken = (token, { broadcast = true } = {}) => {
  accessToken = normalize(token);
  if (accessToken) sessionEndInProgress = false;
  accessTokenRevision += 1;
  emit();
  if (broadcast && channel) {
    channel.postMessage({ type: "token", token: accessToken });
  } else if (broadcast) {
    localStorage.setItem(AUTH_EVENT_KEY, JSON.stringify({ type: "token-updated", at: Date.now() }));
    localStorage.removeItem(AUTH_EVENT_KEY);
  }
};

export const clearAccessToken = ({ broadcast = true, reason = "" } = {}) => {
  accessToken = null;
  if (sessionExpirationTimer) clearTimeout(sessionExpirationTimer);
  sessionExpirationTimer = null;
  sessionExpirationAt = null;
  idleDeadlineAt = null;
  accessTokenRevision += 1;
  emit();
  emitTiming();
  if (broadcast && channel) {
    channel.postMessage({ type: "session-ended", reason });
  } else if (broadcast) {
    localStorage.setItem(
      AUTH_EVENT_KEY,
      JSON.stringify({ type: "session-ended", reason, at: Date.now() })
    );
    localStorage.removeItem(AUTH_EVENT_KEY);
  }
};

export const subscribeToAccessToken = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const subscribeToSessionTiming = (listener) => {
  timingListeners.add(listener);
  return () => timingListeners.delete(listener);
};

export const clearSensitiveClientState = () => {
  // Keep the non-secret firm_login_slug so logout/expiry can return to firm login.
  ["username", "client_id", "case_id", "firm_id", "directoryAdmin", "isAfterLogin"].forEach((key) =>
    localStorage.removeItem(key)
  );
};

const dispatchSessionEnded = (reason) => {
  if (sessionEndInProgress) return;
  sessionEndInProgress = true;
  window.dispatchEvent(new CustomEvent("auth-session-ended", { detail: { reason } }));
};

export const signalSessionEnded = (reason = "session_ended", broadcast = true) => {
  clearAccessToken({ broadcast, reason });
  dispatchSessionEnded(reason);
};

if (channel) {
  channel.addEventListener("message", ({ data }) => {
    if (data?.type === "token") {
      setAccessToken(data.token, { broadcast: false });
    } else if (data?.type === "session-timing") {
      applyTiming(
        {
          nextSessionExpirationAt: data.sessionExpirationAt,
          nextIdleDeadlineAt: data.idleDeadlineAt,
        },
        { broadcast: false }
      );
    } else if (data?.type === "session-ended") {
      clearAccessToken({ broadcast: false });
      dispatchSessionEnded(data.reason);
    }
  });
}

window.addEventListener("storage", (event) => {
  if (event.key !== AUTH_EVENT_KEY || !event.newValue) return;
  let data;
  try {
    data = JSON.parse(event.newValue);
  } catch {
    data = {
      type: "session-ended",
      reason: event.newValue.split(":").slice(1).join(":"),
    };
  }
  if (data.type === "token-updated") {
    window.dispatchEvent(new Event("auth-token-sync-requested"));
  } else if (data.type === "session-timing") {
    applyTiming(
      {
        nextSessionExpirationAt: data.sessionExpirationAt,
        nextIdleDeadlineAt: data.idleDeadlineAt,
      },
      { broadcast: false }
    );
  } else if (data.type === "session-ended") {
    clearAccessToken({ broadcast: false });
    dispatchSessionEnded(data.reason);
  }
});
